/**
 * Utilities for hash-based cache validation
 */

import { apiCall } from '../api/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Check if cached output is valid for current input
 * @param flowId Flow identifier
 * @param stageNum Stage number (1-6)
 * @param currentInput Current input data
 * @returns Object with cache_valid flag and cached_output data
 */
export async function checkCacheValidity(
  flowId: string | null | undefined,
  stageNum: number,
  currentInput: any
): Promise<{ cache_valid: boolean; cached_output: any }> {
  if (!flowId) {
    return { cache_valid: false, cached_output: null };
  }

  try {
    const userEmail = localStorage.getItem('userEmail') || '';
    // Use direct fetch since the backend returns a plain response, not wrapped in ApiResponse
    const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}/check-cache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userEmail ? { 'X-User-Email': userEmail } : {}),
      },
      body: JSON.stringify(currentInput),
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.warn('Cache check failed:', error);
  }

  return { cache_valid: false, cached_output: null };
}

/**
 * Get stage data (input and output) from backend
 * @param flowId Flow identifier
 * @param stageNum Stage number (1-6)
 * @returns Stage data with input and output
 */
export async function getStageData(
  flowId: string | null | undefined,
  stageNum: number
): Promise<{ input: any; output: any } | null> {
  if (!flowId) {
    return null;
  }

  try {
    const userEmail = localStorage.getItem('userEmail') || '';
    // First try to get UI-ready format
    const uiResponse = await fetch(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}/ui`, {
      headers: userEmail ? { 'X-User-Email': userEmail } : undefined,
    });
    
    if (uiResponse.ok) {
      const uiData = await uiResponse.json();
      console.log(`✅ Loaded UI-ready data for stage ${stageNum}`);
      return uiData;
    }
    
    // Fallback to raw format if UI format not available
    const response = await fetch(`${API_BASE_URL}/api/flows/${flowId}/stage/${stageNum}`, {
      headers: userEmail ? { 'X-User-Email': userEmail } : undefined,
    });
    
    if (!response.ok) {
      console.warn(`Failed to get stage ${stageNum} data: HTTP ${response.status}`);
      return null;
    }

    const data = await response.json();
    console.log(`⚠️ Using raw format for stage ${stageNum} (UI format not available)`);
    return data;
  } catch (error) {
    console.warn('Failed to get stage data:', error);
  }

  return null;
}

/**
 * Stage number mapping for different stages
 */
export const STAGE_NUMBERS = {
  QUESTIONS: 1,
  MFR: 2,
  PRODUCTS: 3,
  PSC: 4,
  NAICS: 5,
  VENDORS: 6,
  SECTION_B: 7,
} as const;
